/**
 * @fileoverview React Context provider for the game state, this file exists to prevent prop drilling which was becoming
 * prevalent in the app.
 *
 * @llm IMPORTANT: UI components should not contain game logic.
 * @llm AVOID: there should be no references to specific pieces or piece types in this file.
 * @llm AVOID: zodiac engine method calls other than getGameState or subscribeToGameStateChanges
 *
 * ARCHITECTURAL GUIDELINES:
 * - Component Pattern: Context API provider pattern
 * - Props Interface: All props defined in GameContextProviderProps interface
 * - State Management: Uses Context API with local state hooks
 *
 * RESPONSIBILITIES:
 * - Maintains the UI representation of game state
 * - Manages piece selection and potential moves
 * - Provides game state utilities to components
 */
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  GameState,
  IMove,
  IPiece,
  ISideEffect,
  Mode,
  Position,
  State,
  PieceType,
  MoveType,
} from "../../services/zodiacEngine/types";
import ZodiacEngineService from "../ZodiacEngineService";
import { playMoveSound } from "../../utils/UISoundEffects";
import { AuthContext } from "./AuthContext";
import { actionableEffects } from "../UIConst";
import { EventType, SelectPieceEvent, KingGuessEvent } from "../../services/zodiacEngine/const";
import { EventBridge } from "../Services/EventBridge";
import { updateKingGuess, listenForDocumentChanges } from "../../services/firebase/firebase";

interface SelectedPiece {
  piece: IPiece;
  position: Position;
}

// King Guess taunt feature - allows players to guess opponent's king
export interface KingGuess {
  pieceId: string;
  position: Position;
  guesserIsLight: boolean;
  timestamp: number;
}

export interface GameContextType {
  gameState: GameState;
  updateGameState: (gameState: GameState) => void;

  selectedPiece?: SelectedPiece;
  updateSelectedPiece: (selectedPiece?: SelectedPiece) => void;

  potentialMoves: IMove[];
  updatePotentialMoves: (potentialMoves: IMove[]) => void;

  potentialSideEffects: ISideEffect[];
  updatePotentialSideEffects: (sideEffects: ISideEffect[]) => void;
  // there is not update for this as it is derived from potentialMoves, but adding for animation purposes

  isThisPlayersTurn: () => boolean;
  isLightPlayer: () => boolean;

  // Selection lock state for multi-move sequences
  selectionLocked: boolean;
  setSelectionLocked: (locked: boolean) => void;
  // Helper function to lock/unlock selection
  lockSelection: (lock?: boolean) => void;

  // AI States
  isAIThinking: boolean;
  setIsAIThinking: (isThinking: boolean) => void;

  // Watch Mode States
  isWatchMode: boolean;
  setIsWatchMode: (isWatchMode: boolean) => void;

  // Observe mode - when viewing opponent's piece moves (not your own)
  isObserveMode: boolean;

  // King Guess taunt feature
  lightPlayerGuess: KingGuess | null;
  darkPlayerGuess: KingGuess | null;
  setKingGuess: (position: Position, pieceId: string) => void;
  clearKingGuess: () => void;
  setBotKingGuess: (position: Position, pieceId: string, botIsLight: boolean) => void;
  clearBotKingGuess: (botIsLight: boolean) => void;
  clearAllKingGuesses: () => void;
  getKingGuessForPiece: (pieceId: string) => KingGuess | null;

  // Game ID for multiplayer Firebase sync
  gameId: string | null;
  setGameId: (gameId: string | null) => void;

  // Spectating mode - watching other players' games
  isSpectating: boolean;
  setIsSpectating: (isSpectating: boolean) => void;

  // Show King highlight - temporarily highlights player's king for 3 seconds
  kingHighlightActive: boolean;
  activateKingHighlight: () => void;
}

export const GameContext = createContext<GameContextType>({
  gameState: ZodiacEngineService.zodiacEngine.getGameState(),
  selectedPiece: undefined,
  potentialMoves: [],
  potentialSideEffects: [],
  updatePotentialSideEffects: () => {},
  isLightPlayer: () => true,
  isThisPlayersTurn: () => true,
  selectionLocked: false,
  isAIThinking: false,
  updateGameState: () => {},
  updateSelectedPiece: () => {},
  updatePotentialMoves: () => {},
  setSelectionLocked: () => {},
  lockSelection: () => {},
  setIsAIThinking: () => {},
  isWatchMode: false,
  setIsWatchMode: () => {},
  isObserveMode: false,
  // King Guess defaults
  lightPlayerGuess: null,
  darkPlayerGuess: null,
  setKingGuess: () => {},
  clearKingGuess: () => {},
  setBotKingGuess: () => {},
  clearBotKingGuess: () => {},
  clearAllKingGuesses: () => {},
  getKingGuessForPiece: () => null,
  // Game ID defaults
  gameId: null,
  setGameId: () => {},
  // Spectating defaults
  isSpectating: false,
  setIsSpectating: () => {},
  // Show King defaults
  kingHighlightActive: false,
  activateKingHighlight: () => {},
});

interface GameContextProviderProps {
  children: ReactNode;
}

export function GameContextProvider(props: GameContextProviderProps) {
  const [gameState, setGameState] = useState<GameState>(
    ZodiacEngineService.zodiacEngine.getGameState()
  );
  const [selectedPiece, setSelectedPiece] = useState<SelectedPiece | undefined>(
    undefined
  );
  const [potentialMoves, setPotentialMoves] = useState<IMove[]>([]);
  const [potentialSideEffects, setPotentialSideEffects] = useState<
    ISideEffect[]
  >([]);
  const [selectionLocked, setSelectionLocked] = useState<boolean>(false);

  // Mirror of selectedPiece for reading from inside the empty-deps useEffect
  // that subscribes to engine state changes (its closure captures the initial
  // undefined and never updates).
  const selectedPieceRef = useRef<SelectedPiece | undefined>(undefined);
  selectedPieceRef.current = selectedPiece;

  // Tracks the last gameplay-relevant state signature to avoid clearing selection
  // on non-gameplay Firebase updates (heartbeat, presence, chat, time sync, etc.)
  const lastGameplaySignatureRef = useRef<string>('');
  // Last move seen by the sound hook below — starts empty so the first notification
  // after mount is treated as "arrived", not as a move.
  const lastMoveSignatureRef = useRef<string>('');

  const [isAIThinking, setIsAIThinking] = useState<boolean>(false);
  const [isNNTestMode, setIsNNTestMode] = useState<boolean>(false);
  const [isWatchMode, setIsWatchMode] = useState<boolean>(false);
  const { user } = useContext(AuthContext);

  // King Guess taunt feature state
  const [lightPlayerGuess, setLightPlayerGuess] = useState<KingGuess | null>(null);
  const [darkPlayerGuess, setDarkPlayerGuess] = useState<KingGuess | null>(null);

  // Game ID for multiplayer Firebase sync
  const [gameId, setGameId] = useState<string | null>(null);

  // Spectating mode - watching other players' games
  const [isSpectating, setIsSpectating] = useState<boolean>(false);

  // Show King highlight - temporarily highlights player's king for 3 seconds
  const [kingHighlightActive, setKingHighlightActive] = useState<boolean>(false);
  const kingHighlightTimerRef = useRef<NodeJS.Timeout | null>(null);

  const activateKingHighlight = () => {
    if (kingHighlightActive) return;
    setKingHighlightActive(true);
    if (kingHighlightTimerRef.current) {
      clearTimeout(kingHighlightTimerRef.current);
    }
    kingHighlightTimerRef.current = setTimeout(() => {
      setKingHighlightActive(false);
      kingHighlightTimerRef.current = null;
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (kingHighlightTimerRef.current) {
        clearTimeout(kingHighlightTimerRef.current);
      }
    };
  }, []);

  // Listen for king guess updates from Firebase (opponent's guesses)
  useEffect(() => {
    if (!gameId || gameState.mode !== Mode.Live) return;

    const unsubscribe = listenForDocumentChanges(gameId, (data) => {
      if (!data) return;


      // Update light player's guess from Firebase
      // Only update if the field EXISTS in the document (use 'in' operator)
      if ('lightPlayerKingGuess' in data) {
        const firebaseGuess = data.lightPlayerKingGuess;
        if (firebaseGuess) {
          setLightPlayerGuess({
            pieceId: firebaseGuess.pieceId,
            position: { row: firebaseGuess.position.row, col: firebaseGuess.position.col },
            guesserIsLight: true,
            timestamp: firebaseGuess.timestamp,
          });
        } else {
          setLightPlayerGuess(null);
        }
      }

      // Update dark player's guess from Firebase
      // Only update if the field EXISTS in the document (use 'in' operator)
      if ('darkPlayerKingGuess' in data) {
        const firebaseGuess = data.darkPlayerKingGuess;
        if (firebaseGuess) {
          setDarkPlayerGuess({
            pieceId: firebaseGuess.pieceId,
            position: { row: firebaseGuess.position.row, col: firebaseGuess.position.col },
            guesserIsLight: false,
            timestamp: firebaseGuess.timestamp,
          });
        } else {
          setDarkPlayerGuess(null);
        }
      }
    });

    return () => unsubscribe?.();
  }, [gameId, gameState.mode]);

  // Listen for AI king guess events via EventManager
  useEffect(() => {
    const eventManager = ZodiacEngineService.zodiacEngine.getEventManager();

    const handleKingGuessEvent = (event: any) => {
      if (event.type === EventType.kingGuess) {
        const guessEvent = event as KingGuessEvent;

        const guess: KingGuess = {
          pieceId: guessEvent.guess.pieceId,
          position: { row: guessEvent.guess.position.row, col: guessEvent.guess.position.col },
          guesserIsLight: guessEvent.guesserIsLight,
          timestamp: guessEvent.timestamp,
        };

        if (guessEvent.guesserIsLight) {
          setLightPlayerGuess(guess);
        } else {
          setDarkPlayerGuess(guess);
        }
      }
    };

    const unsubscribe = eventManager.subscribe(handleKingGuessEvent);
    return () => unsubscribe();
  }, []);

  // Phase 3 - SOC: previousTurn/retryCount removed, now handled by AIScheduler

  useEffect(() => {
    // Game state has been updated; register a listener
    const updateUiState = () => {
      // Get the current game state when notified of a change
      const newGameState = ZodiacEngineService.zodiacEngine.getGameState();

      // CRITICAL: Spread to create a new object reference so React detects the change.
      // The engine mutates gameState in place and getGameState() returns the same ref,
      // so without spreading, React's Object.is check bails out and skips re-renders.
      // Also preserve challengeAIConfig (UI-only property the engine doesn't know about).
      setGameState((prevState) => {
        const prevConfig = (prevState as any).challengeAIConfig;
        const nextState = { ...newGameState };
        if (prevConfig) {
          (nextState as any).challengeAIConfig = prevConfig;
        }
        return nextState;
      });

      // Get the current game state to check for bonus turn
      const currentState = ZodiacEngineService.zodiacEngine.getGameState();

      // Check if this is an actual gameplay change (move, turn, state transition)
      // vs. a non-gameplay Firebase update (heartbeat, presence, chat, time sync).
      // Only clear selection/potentialMoves when the game actually changed.
      const gameplaySignature = `${currentState.turnCount}-${currentState.isLightTurn}-${currentState.state}-${currentState.activeBonusTurnPieceId || ''}-${currentState.fenHistory?.length || 0}`;
      const isGameplayChange = gameplaySignature !== lastGameplaySignatureRef.current;
      lastGameplaySignatureRef.current = gameplaySignature;

      // A PIECE MOVED — the one place in the UI that sees every move of every mode.
      //
      // This listener is called on every engine notification, local and remote, so a
      // single check here covers the player's own moves, the AI's replies, a remote
      // opponent's move arriving over Firebase, tutorials, puzzles and replays — rather
      // than a play() call sprinkled through every screen's move handler.
      //
      // TWO SIGNALS, because neither is enough alone: `lastMove` is rewritten by
      // zodiacEngine.makeMove on every successful move INCLUDING bonus turns (a monkey
      // swing doesn't advance the turn), while `turnCount` catches a remote move whose
      // synced state carries no lastMove. Either one changing is one move.
      //
      // Playing only, and never on the first notification after mount — otherwise
      // opening a board mid-game would announce a move nobody made.
      const lm = (currentState as any).lastMove;
      const moveSignature = lm
        ? `${currentState.turnCount}|${lm.fromPosition?.row},${lm.fromPosition?.col}>${lm.toPosition?.row},${lm.toPosition?.col}|${lm.mover?.type}|${lm.mover?.isLight}`
        : `${currentState.turnCount}|none`;
      if (
        lastMoveSignatureRef.current !== '' &&
        moveSignature !== lastMoveSignatureRef.current &&
        currentState.state === State.Playing
      ) {
        playMoveSound();
      }
      lastMoveSignatureRef.current = moveSignature;


      if (isGameplayChange) {
        // CRITICAL FIX: Don't clear potential moves during bonus turn if we have the active piece selected
        // This prevents the board from becoming unresponsive in multiplayer after a bonus turn is granted
        if (currentState.skipTurnChange && currentState.activeBonusTurnPieceId) {
          if (selectedPiece && selectedPiece.piece.id === currentState.activeBonusTurnPieceId) {
            // Don't clear potential moves - they're still valid for the bonus turn
          } else if (!selectedPiece) {
            // SAFETY VALVE (Layer 1): Bonus turn is active but selectedPiece is undefined
            // (race condition from movePiece clearing selection before auto-select fires).
            // Don't clear potentialMoves - let the auto-select event repopulate them.
          } else {
            setPotentialMoves([]); // Clear if wrong piece selected
          }
        } else {
          // Normal case: clear potential moves on game state change
          setPotentialMoves([]);
        }

        // Reset selection lock when game state changes (turn ends)
        // Unless the skipTurnChange flag is set (bonus turn in progress)
        if (!currentState.skipTurnChange) {
          setSelectionLocked(false);

          // GUARD: Clear stale selection on turn change.
          // Covers two cases:
          //   1. Stale BonusTurnHandler setTimeout leaving an opponent's piece selected/locked.
          //   2. AI bonus-turn moves (SwingMoveHandler emits selectPiece) where no UI handler
          //      runs to clear the highlight when the bonus turn ends.
          // Reads selectedPieceRef.current rather than the stale closure-captured
          // `selectedPiece`, which is always undefined here (empty-deps useEffect).
          const liveSelectedPiece = selectedPieceRef.current;
          if (liveSelectedPiece && liveSelectedPiece.piece.isLight !== currentState.isLightTurn) {
            setSelectedPiece(undefined);
            setPotentialMoves([]);
            setPotentialSideEffects([]);
          }
        }
      } else {
        // Non-gameplay update (heartbeat, presence, chat, time sync).
        // Refresh selectedPiece to reference the equivalent piece from the
        // new game state, preventing stale object references that cause
        // piece duplication when making a move after a Firebase sync.
        // Match by position + type + color since Firebase sync may change piece IDs.
        setSelectedPiece((prev) => {
          if (!prev) return prev;
          const square = currentState.board[prev.position.row]?.[prev.position.col];
          if (!square) {
            // SAFETY VALVE (Layer 1): If we'd lose selection during an active bonus turn,
            // search the board for the bonus turn piece instead of returning undefined.
            if (currentState.skipTurnChange && currentState.activeBonusTurnPieceId) {
              for (let r = 0; r < currentState.board.length; r++) {
                for (let c = 0; c < currentState.board[r].length; c++) {
                  const p = currentState.board[r][c].Pieces.find(
                    (p) => p.id === currentState.activeBonusTurnPieceId
                  );
                  if (p) {
                    return { piece: p, position: { row: r, col: c } };
                  }
                }
              }
            }
            return undefined;
          }
          const freshPiece = square.Pieces.find(
            (p) => p.type === prev.piece.type && p.isLight === prev.piece.isLight
          );
          if (freshPiece) {
            return { piece: freshPiece, position: prev.position };
          }
          // SAFETY VALVE (Layer 1): Piece not found at expected position during bonus turn.
          // Search the entire board for the bonus turn piece before giving up.
          if (currentState.skipTurnChange && currentState.activeBonusTurnPieceId) {
            for (let r = 0; r < currentState.board.length; r++) {
              for (let c = 0; c < currentState.board[r].length; c++) {
                const p = currentState.board[r][c].Pieces.find(
                  (p) => p.id === currentState.activeBonusTurnPieceId
                );
                if (p) {
                  return { piece: p, position: { row: r, col: c } };
                }
              }
            }
          }
          return undefined;
        });
      }
    };

    // Subscribe to game state changes
    ZodiacEngineService.zodiacEngine.subscribeToGameStateChanges(updateUiState);

    // Add event listener for automatic piece selection
    const handleZodiacEvent = (event: CustomEvent) => {
      const eventData = event.detail;


      if (eventData.type === EventType.selectPiece) {
        const selectEvent = eventData as SelectPieceEvent;


        // Find the piece by ID
        const currentGameState =
          ZodiacEngineService.zodiacEngine.getGameState();

        let foundPiece = false;
        let targetPiece: IPiece | null = null;
        let targetPosition: Position | null = null;

        // First try to find by ID
        for (let row = 0; row < currentGameState.board.length; row++) {
          for (let col = 0; col < currentGameState.board[row].length; col++) {
            const square = currentGameState.board[row][col];
            const piece = square.Pieces.find(
              (p) => p.id === selectEvent.pieceId
            );

            if (piece) {
              foundPiece = true;
              targetPiece = piece;
              targetPosition = { row, col };
              break;
            }
          }
          if (foundPiece) break;
        }

        // FALLBACK: If not found by ID (Firebase sync changed IDs), use position
        if (!foundPiece && selectEvent.position) {
          const square = currentGameState.board[selectEvent.position.row][selectEvent.position.col];
          if (square.Pieces.length > 0) {
            // Get the piece that matches the bonus turn ID (if any)
            targetPiece = square.Pieces.find(p => p.id === currentGameState.activeBonusTurnPieceId) || square.Pieces[0];
            targetPosition = selectEvent.position;
            foundPiece = true;
          }
        }

        if (foundPiece && targetPiece && targetPosition) {
          const piece = targetPiece;
          const piecePosition = targetPosition;
          // Auto-select the piece and show its valid moves
          setSelectedPiece({
            piece,
            position: piecePosition,
          });

          const currentGameState = ZodiacEngineService.zodiacEngine.getGameState();
          const validMoves = ZodiacEngineService.zodiacEngine.getValidMoves(
            piecePosition,
            piece
          );

          // CRITICAL: Auto-end turn if Virus has NO infection moves during bonus turn
          // "Why waste the players turn forcing them to click the singular 'option'" - User
          // Check for: no moves at all, OR only the endTurn move (no real infection targets)
          const onlyEndTurnAvailable = validMoves.length === 1 && validMoves[0].moveType === MoveType.endTurn;
          if ((validMoves.length === 0 || onlyEndTurnAvailable) && currentGameState.activeBonusTurnPieceId === piece.id) {

            // Execute an end-turn move to properly capture the host piece
            const square = currentGameState.board[piecePosition.row][piecePosition.col];
            const virusPiece = square.Pieces.find(p => p.id === piece.id);
            const hostPiece = square.Pieces.find(p => p.id !== piece.id) || null;

            if (virusPiece) {
              ZodiacEngineService.zodiacEngine.makeMove(
                piecePosition,
                piecePosition, // Same position (end turn)
                virusPiece,
                hostPiece,
                MoveType.endTurn,
                []
              ).then(() => {
              });
            }
            return;
          }

          setPotentialMoves(validMoves);
          setPotentialSideEffects(findSideEffectsNotInMoves(validMoves));


          // Check if we should lock the selection
          if (selectEvent.lockSelection) {
            setSelectionLocked(true);
          }
          return;
        }

        if (!foundPiece) {
        }
      }
    };

    // Register the event listener
    document.addEventListener(
      "zodiac-event",
      handleZodiacEvent as EventListener
    );

    // Initialize EventBridge to bridge engine events to DOM
    EventBridge.initialize(ZodiacEngineService.zodiacEngine.getEventManager());

    // Cleanup subscription on unmount
    return () => {
      ZodiacEngineService.zodiacEngine.unsubscribeToGameStateChanges(
        updateUiState
      );
      document.removeEventListener(
        "zodiac-event",
        handleZodiacEvent as EventListener
      );
      EventBridge.cleanup();
    };
  }, []);

  // Add effect to detect game end and auto-restart for NN Test mode
  useEffect(() => {
    // Check if game has ended and we're in NN test mode
    if (
      isNNTestMode &&
      gameState.mode === Mode.AI &&
      gameState.state === State.PostGame
    ) {
      setTimeout(() => {
        // Reset game and start a new game
        ZodiacEngineService.zodiacEngine.resetAndStartNewGame();
        // Update game state after resetting
        setGameState(ZodiacEngineService.zodiacEngine.getGameState());
      }, 100); // 100ms for near-instant restart
    }
  }, [gameState.state, gameState.mode, isNNTestMode]);

  // Phase 3 - SOC: Retry loop detection removed, now handled by AIScheduler internally

  // ==========================================
  // Phase 3 - SOC Refactoring: AIScheduler Integration
  // ==========================================
  // Centralized AI move scheduling across Watch, TestAI, and Play vs AI modes
  // Replaces 3 separate useEffect hooks (~250 lines) with single manager integration

  useEffect(() => {
    const aiScheduler = ZodiacEngineService.zodiacEngine.aiScheduler;


    // Configure AIScheduler based on active mode
    if (isWatchMode && gameState.mode === Mode.Watch) {
      aiScheduler.startWatchMode(2000);
    } else if (isNNTestMode && (gameState.mode === Mode.AI || gameState.mode === Mode.TestAI)) {
      aiScheduler.startTestMode(10);
    } else if (!isWatchMode && !isNNTestMode && !gameState.isBotGame && (gameState.mode === Mode.AI || gameState.mode === Mode.TestAI || gameState.mode === Mode.Tutorial)) {
      aiScheduler.startPlayVsAIMode(10);
    } else {
      aiScheduler.stop();
    }

    // Subscribe to AI thinking state changes
    const unsubscribeThinking = aiScheduler.onThinkingStateChange((thinking) => {
      setIsAIThinking(thinking);
    });

    const unsubscribeError = aiScheduler.onError((error: Error) => {
    });

    // Cleanup on unmount or mode change
    return () => {
      unsubscribeThinking();
      unsubscribeError();
    };
  }, [isWatchMode, isNNTestMode, gameState.mode, gameState.isBotGame]);

  // Trigger AIScheduler to check and execute moves when game state changes
  useEffect(() => {
    const aiScheduler = ZodiacEngineService.zodiacEngine.aiScheduler;

    // Let AIScheduler decide if it should make a move
    aiScheduler.checkAndExecuteAIMove(gameState);
  }, [
    gameState.turnCount,
    gameState.isLightTurn,
    gameState.state,
    gameState.mode,
    gameState.activeBonusTurnPieceId,
  ]);

  // Add effect to detect game end and auto-restart for Watch mode
  useEffect(() => {
    // Check if game has ended and we're in watch mode
    if (
      isWatchMode &&
      gameState.mode === Mode.Watch &&
      (gameState.state === State.PostGame || gameState.state === State.Draw)
    ) {
      setTimeout(() => {
        // Generate new random kings for the next Watch mode game
        const eligibleKings = [
          PieceType.Bear,
          PieceType.Crane,
          PieceType.Frog,
          PieceType.Goat,
          PieceType.Monkey,
          PieceType.Fungi,
          PieceType.Rhino,
          PieceType.Snake,
          PieceType.Tree,
          PieceType.Wolf,
          PieceType.Octopus,
          PieceType.Penguin,
        ];

        const randomLightKingIndex = Math.floor(
          Math.random() * eligibleKings.length
        );
        const lightKing = eligibleKings[randomLightKingIndex];

        const randomDarkKingIndex = Math.floor(
          Math.random() * eligibleKings.length
        );
        const darkKing = eligibleKings[randomDarkKingIndex];


        // Reset and start a new Watch mode game (not regular AI mode)
        ZodiacEngineService.zodiacEngine.switchToWatchMode(lightKing, darkKing);

        // Update game state after resetting
        setGameState(ZodiacEngineService.zodiacEngine.getGameState());
      }, 3000); // 3-second delay before starting new game
    }
  }, [gameState.state, gameState.mode, isWatchMode]);

  // Method should look through all of the potential moves, find any which contain side effects that are not
  // in the same square as the move, make a list of them and return them
  function findSideEffectsNotInMoves(moves: IMove[]): ISideEffect[] {
    // collects side effect from moves
    const movesSideEffects = moves
      .map((move) => move.sideEffects)
      .flat()
      .filter((se) => actionableEffects.includes(se.effect.type));
  
    return movesSideEffects;
  }

  const isThisPlayersTurn = (): boolean => {
    // Spectators can never make moves
    if (isSpectating) {
      return false;
    }

    if (gameState.mode === Mode.AI || gameState.mode === Mode.TestAI || gameState.mode === Mode.Tutorial) {
      // In AI or TestAI mode, return true only if it's the human player's turn (light) or in test mode
      // If AI is thinking, it's not the player's turn
      if (isNNTestMode) {
        return true; // Always player's turn in test mode
      }

      // Check for Local 2P mode (both players are human) via challengeAIConfig
      const challengeConfig = (gameState as any).challengeAIConfig;
      if (challengeConfig) {
        const { lightPlayer, darkPlayer } = challengeConfig;
        // In Local 2P mode, both players share the device
        // Return true but the selected piece color must match the current turn
        // This is enforced by only showing valid moves for pieces of the current turn's color
        if (lightPlayer === 'player' && darkPlayer === 'player') {
          return true;
        }
      }

      const playerIsLight = isLightPlayer();
      const result = (gameState.isLightTurn === playerIsLight) && !isAIThinking;
      return result; // Only player's turn if it matches their color and AI is not thinking
    }

    if (gameState.mode === Mode.Watch) {
      // In Watch mode, it's never the player's turn (AI vs AI)
      return false;
    }

    const playerIsLight = isLightPlayer();

    // CRITICAL: During bonus turns, the turn stays with the same player
    // Check if there's an active bonus turn and if so, keep turn with bonus turn player
    if (gameState.skipTurnChange && gameState.activeBonusTurnPieceId) {
      // During bonus turn, find the bonus turn piece to determine its color
      // Search through all pieces on the board to find the bonus turn piece
      let bonusTurnPiece = null;
      for (const row of gameState.board) {
        for (const square of row) {
          for (const piece of square.Pieces) {
            if (piece.id === gameState.activeBonusTurnPieceId) {
              bonusTurnPiece = piece;
              break;
            }
          }
          if (bonusTurnPiece) break;
        }
        if (bonusTurnPiece) break;
      }

      if (bonusTurnPiece) {
        // If the bonus turn piece matches this player's color, it's still their turn
        const bonusTurnIsLight = bonusTurnPiece.isLight;
        const result = bonusTurnIsLight === playerIsLight;
        return result;
      }
    }

    const result = gameState.isLightTurn === playerIsLight;
    return result;
  };

  const isLightPlayer = (): boolean => {
    if (!user || !gameState.lightPlayer) {
      // Default to light in developer mode or when not set
      return true;
    }
    // If there's a user and the game has a designated light player, check if they match
    return user.uid === gameState.lightPlayer;
  };

  /**
   * Locks selection to the current piece
   * Useful for multi-move pieces to prevent cheating
   * @param lock Whether to lock (true) or unlock (false) selection
   */
  const lockSelection = (lock: boolean = true) => {
    setSelectionLocked(lock);
  };

  /**
   * Debug the state of the selection lock
   * Check if the game state and GameContext are in sync
   */
  useEffect(() => {
  }, [selectionLocked, gameState.skipTurnChange]);

  /**
   * King Guess taunt feature - set a guess on an opponent's piece
   * Each player can only have one active guess at a time
   */
  const setKingGuess = (position: Position, pieceId: string) => {
    const playerIsLight = isLightPlayer();
    const guess: KingGuess = {
      pieceId,
      position,
      guesserIsLight: playerIsLight,
      timestamp: Date.now(),
    };

    if (playerIsLight) {
      setLightPlayerGuess(guess);
    } else {
      setDarkPlayerGuess(guess);
    }


    // Sync to Firebase in multiplayer mode
    if (gameState.mode === Mode.Live && gameId) {
      updateKingGuess(gameId, playerIsLight, {
        pieceId,
        position: { row: position.row, col: position.col },
        timestamp: guess.timestamp,
      });
    } else {
    }
  };

  /**
   * Clear the current player's king guess
   */
  const clearKingGuess = () => {
    const playerIsLight = isLightPlayer();
    if (playerIsLight) {
      setLightPlayerGuess(null);
    } else {
      setDarkPlayerGuess(null);
    }

    // Sync to Firebase in multiplayer mode
    if (gameState.mode === Mode.Live && gameId) {
      updateKingGuess(gameId, playerIsLight, null);
    }
  };

  /**
   * Set a king guess for a bot player (explicit color, no user check)
   */
  const setBotKingGuess = (position: Position, pieceId: string, botIsLight: boolean) => {
    const guess: KingGuess = {
      pieceId,
      position,
      guesserIsLight: botIsLight,
      timestamp: Date.now(),
    };
    if (botIsLight) {
      setLightPlayerGuess(guess);
    } else {
      setDarkPlayerGuess(guess);
    }
  };

  /**
   * Clear king guess for a bot player (explicit color)
   */
  const clearBotKingGuess = (botIsLight: boolean) => {
    if (botIsLight) {
      setLightPlayerGuess(null);
    } else {
      setDarkPlayerGuess(null);
    }
  };

  /**
   * Clear ALL king guesses (both players) - used when starting a new game
   */
  const clearAllKingGuesses = () => {
    setLightPlayerGuess(null);
    setDarkPlayerGuess(null);
  };

  /**
   * Get king guess for a specific piece (if any player guessed it)
   */
  const getKingGuessForPiece = (pieceId: string): KingGuess | null => {
    if (lightPlayerGuess?.pieceId === pieceId) {
      return lightPlayerGuess;
    }
    if (darkPlayerGuess?.pieceId === pieceId) {
      return darkPlayerGuess;
    }
    return null;
  };

  /**
   * Compute observe mode - true when viewing opponent's piece moves in a multiplayer match
   * This triggers greyed-out move indicators so players know they're observing, not controlling
   */
  const computeIsObserveMode = (): boolean => {
    // No observe mode if no piece selected
    if (!selectedPiece) {
      return false;
    }

    // No observe mode in Sandbox, Dev, Watch, or Rules modes (player controls both sides or just watching)
    if (
      gameState.mode === Mode.Dev ||
      gameState.mode === Mode.Sandbox ||
      gameState.mode === Mode.Watch ||
      gameState.mode === Mode.Rules
    ) {
      return false;
    }

    // In AI/TestAI/Tutorial modes, check for Local 2P first
    if (gameState.mode === Mode.AI || gameState.mode === Mode.TestAI || gameState.mode === Mode.Tutorial) {
      // Check for Local 2P mode (both players are human)
      const challengeConfig = (gameState as any).challengeAIConfig;
      if (challengeConfig) {
        const { lightPlayer, darkPlayer } = challengeConfig;
        // In Local 2P mode, observe mode when selecting piece of the OTHER player's color
        if (lightPlayer === 'player' && darkPlayer === 'player') {
          const isZombified = selectedPiece.piece.zombifiedStatus?.isZombified || false;
          const effectivelyLight = isZombified ? !selectedPiece.piece.isLight : selectedPiece.piece.isLight;
          // Observe mode if piece color doesn't match current turn
          const result = effectivelyLight !== gameState.isLightTurn;
          return result;
        }
      }

      // Standard AI mode: only light player is controlled by human
      // Dark pieces are AI-controlled, so viewing them is observe mode
      const isZombified = selectedPiece.piece.zombifiedStatus?.isZombified || false;
      // If zombified, Dark piece is controlled by Light (human), so NOT observe mode
      const effectivelyLight = isZombified ? !selectedPiece.piece.isLight : selectedPiece.piece.isLight;
      return !effectivelyLight;
    }

    // In Live/Multiplayer mode, check if selected piece belongs to opponent
    const playerIsLight = isLightPlayer();
    const pieceIsLight = selectedPiece.piece.isLight;

    // Check if piece is zombified (controlled by opponent for one turn)
    const isZombified = selectedPiece.piece.zombifiedStatus?.isZombified || false;

    // If zombified, the piece is controlled by the OPPOSITE team
    const effectivelyControlledByLight = isZombified ? !pieceIsLight : pieceIsLight;

    // Observe mode when piece is NOT controlled by current player
    const result = effectivelyControlledByLight !== playerIsLight;
    return result;
  };

  return (
    <GameContext.Provider
      value={{
        gameState,
        updateGameState: setGameState,
        selectedPiece,
        updateSelectedPiece: setSelectedPiece,
        potentialMoves,
        updatePotentialMoves: (newMoves: IMove[]) => {
          setPotentialMoves(newMoves);
          setPotentialSideEffects(findSideEffectsNotInMoves(newMoves));
        },
        potentialSideEffects,
        updatePotentialSideEffects: (sideEffects: ISideEffect[]) => {
          setPotentialSideEffects(sideEffects);
        },
        isThisPlayersTurn,
        isLightPlayer,
        selectionLocked,
        setSelectionLocked,
        lockSelection,
        isAIThinking,
        setIsAIThinking,
        isWatchMode,
        setIsWatchMode,
        isObserveMode: computeIsObserveMode(),
        // King Guess taunt feature
        lightPlayerGuess,
        darkPlayerGuess,
        setKingGuess,
        clearKingGuess,
        setBotKingGuess,
        clearBotKingGuess,
        clearAllKingGuesses,
        getKingGuessForPiece,
        // Game ID for multiplayer
        gameId,
        setGameId,
        // Spectating mode
        isSpectating,
        setIsSpectating,
        // Show King highlight
        kingHighlightActive,
        activateKingHighlight,
      }}
    >
      {props.children}
    </GameContext.Provider>
  );
}
